import React, { useState, useEffect } from 'react';
import { adminAPI, coursesAPI, leadersAPI, placementsAPI, contactsAPI } from '../api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ 
    courses: 0, 
    leaders: 0, 
    placements: 0, 
    gallery: 0,
    contacts: 0,
    virtualTours: 0,
    campusVisits: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentCourses, setRecentCourses] = useState([]);
  const [recentPlacements, setRecentPlacements] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateData, setUpdateData] = useState({ status: '', adminNotes: '', assignedTo: '' });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching dashboard data...');
      
      // Fetch stats from admin endpoint
      try {
        const statsRes = await adminAPI.getStats();
        console.log('Stats response:', statsRes.data);
        if (statsRes.data && statsRes.data.data) {
          setStats(prev => ({
            ...prev,
            ...statsRes.data.data
          }));
        } else if (statsRes.data) {
          setStats(prev => ({
            ...prev,
            ...statsRes.data
          }));
        }
      } catch (statsError) {
        console.error('Stats error:', statsError);
      }
      
      // Fetch contact inquiries
      try {
        const contactsRes = await contactsAPI.getAll();
        console.log('Full contacts response:', contactsRes);
        
        // Handle different response structures
        let contacts = [];
        if (contactsRes.data && Array.isArray(contactsRes.data)) {
          contacts = contactsRes.data;
        } else if (contactsRes.data && contactsRes.data.data && Array.isArray(contactsRes.data.data)) {
          contacts = contactsRes.data.data;
        } else if (Array.isArray(contactsRes)) {
          contacts = contactsRes;
        }
        
        console.log('Parsed contacts:', contacts);
        
        if (contacts.length > 0) {
          setStats(prev => ({
            ...prev,
            contacts: contacts.length || 0,
            virtualTours: contacts.filter(c => c.inquiryType === 'virtual_tour').length,
            campusVisits: contacts.filter(c => c.inquiryType === 'schedule_visit').length
          }));
          // Get recent contacts (last 5)
          setRecentContacts(contacts.slice(0, 5));
        } else {
          console.log('No contacts found');
          setRecentContacts([]);
        }
      } catch (contactsError) {
        console.error('Contacts error:', contactsError);
        toast.error('Failed to fetch contacts');
        setRecentContacts([]);
      }
      
      // Fetch recent courses
      try {
        const coursesRes = await coursesAPI.getAllAdmin();
        console.log('Courses response:', coursesRes.data);
        let courses = [];
        if (coursesRes.data && Array.isArray(coursesRes.data)) {
          courses = coursesRes.data;
        } else if (coursesRes.data && coursesRes.data.data && Array.isArray(coursesRes.data.data)) {
          courses = coursesRes.data.data;
        }
        setRecentCourses(courses.slice(0, 5));
      } catch (coursesError) {
        console.error('Courses error:', coursesError);
        toast.error('Failed to fetch courses');
      }
      
      // Fetch recent placements
      try {
        const placementsRes = await placementsAPI.getAllAdmin();
        console.log('Placements response:', placementsRes.data);
        let placements = [];
        if (placementsRes.data && Array.isArray(placementsRes.data)) {
          placements = placementsRes.data;
        } else if (placementsRes.data && placementsRes.data.data && Array.isArray(placementsRes.data.data)) {
          placements = placementsRes.data.data;
        }
        setRecentPlacements(placements.slice(0, 5));
      } catch (placementsError) {
        console.error('Placements error:', placementsError);
        toast.error('Failed to fetch placements');
      }
      
    } catch (error) {
      console.error('Dashboard error:', error);
      setError(error.message);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers for stats cards
  const navigateTo = (path) => {
    navigate(path);
  };

  const handleUpdateStatus = async (id, status, adminNotes = '', assignedTo = '') => {
    try {
      await contactsAPI.updateStatus(id, { status, adminNotes, assignedTo });
      toast.success('Contact updated successfully');
      fetchDashboardData();
      setShowUpdateModal(false);
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    }
  };

  const handleDeleteContact = async (id) => {
    if (window.confirm('Are you sure you want to delete this contact inquiry?')) {
      try {
        await contactsAPI.delete(id);
        toast.success('Contact deleted successfully');
        fetchDashboardData();
      } catch (error) {
        console.error('Error deleting contact:', error);
        toast.error('Failed to delete contact');
      }
    }
  };

  const viewDetails = (contact) => {
    setSelectedContact(contact);
    setShowDetailsModal(true);
  };

  const openUpdateModal = (contact) => {
    setSelectedContact(contact);
    setUpdateData({
      status: contact.status,
      adminNotes: contact.adminNotes || '',
      assignedTo: contact.assignedTo || ''
    });
    setShowUpdateModal(true);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#ffc107';
      case 'contacted': return '#17a2b8';
      case 'confirmed': return '#28a745';
      case 'completed': return '#20c997';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getAvatarColor = (type) => {
    switch(type) {
      case 'virtual_tour': return '#00B4D8';
      case 'schedule_visit': return '#0077B6';
      default: return '#0096C7';
    }
  };

  const getInquiryTypeLabel = (type) => {
    switch(type) {
      case 'virtual_tour': return 'Virtual Tour';
      case 'schedule_visit': return 'Campus Visit';
      case 'general': return 'General Inquiry';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-content">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="btn-retry">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Welcome Section */}
      <div className="welcome-section">
        <div className="welcome-content">
          <h1>Welcome Back, Admin</h1>
          <p>Here's what's happening with your platform today</p>
        </div>
        <div className="date-badge">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
      </div>
      
      {/* Stats Grid - Compact Version with Clickable Cards */}
      <div className="stats-grid-compact">
        <div 
          className="stat-card-compact clickable" 
          onClick={() => navigateTo('/courses')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon-compact">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>
            </svg>
          </div>
          <div className="stat-info-compact">
            <span className="stat-label">Courses</span>
            <span className="stat-number-compact">{stats.courses || 0}</span>
          </div>
          <div className="stat-arrow">→</div>
        </div>
        
        <div 
          className="stat-card-compact clickable" 
          onClick={() => navigateTo('/leaders')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon-compact">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
          </div>
          <div className="stat-info-compact">
            <span className="stat-label">Leaders</span>
            <span className="stat-number-compact">{stats.leaders || 0}</span>
          </div>
          <div className="stat-arrow">→</div>
        </div>
        
        <div 
          className="stat-card-compact clickable" 
          onClick={() => navigateTo('/placements')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon-compact">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <div className="stat-info-compact">
            <span className="stat-label">Placements</span>
            <span className="stat-number-compact">{stats.placements || 0}</span>
          </div>
          <div className="stat-arrow">→</div>
        </div>
        
        <div 
          className="stat-card-compact clickable" 
          onClick={() => navigateTo('/contacts')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon-compact">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
          </div>
          <div className="stat-info-compact">
            <span className="stat-label">Inquiries</span>
            <span className="stat-number-compact">{stats.contacts || 0}</span>
          </div>
          <div className="stat-arrow">→</div>
        </div>
        
        <div 
          className="stat-card-compact clickable" 
          onClick={() => navigateTo('/contacts?type=virtual_tour')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon-compact">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
            </svg>
          </div>
          <div className="stat-info-compact">
            <span className="stat-label">Virtual Tours</span>
            <span className="stat-number-compact">{stats.virtualTours || 0}</span>
          </div>
          <div className="stat-arrow">→</div>
        </div>
        
        <div 
          className="stat-card-compact clickable" 
          onClick={() => navigateTo('/contacts?type=schedule_visit')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon-compact">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
              <path d="M17 21v-4H7v4"/>
              <path d="M12 7v6"/>
              <path d="M9 10h6"/>
            </svg>
          </div>
          <div className="stat-info-compact">
            <span className="stat-label">Campus Visits</span>
            <span className="stat-number-compact">{stats.campusVisits || 0}</span>
          </div>
          <div className="stat-arrow">→</div>
        </div>
      </div>

      {/* Recent Contacts Card */}
      <div className="data-card">
        <div className="card-header">
          <div className="header-left">
            <div className="header-icon contacts-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </div>
            <div>
              <h3>Recent Contact Inquiries</h3>
              <p>Latest messages and booking requests</p>
            </div>
          </div>
          <button className="view-all-btn" onClick={() => navigateTo('/contacts')}>View All →</button>
        </div>
        
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Email/Phone</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recentContacts.length > 0 ? (
                recentContacts.map((contact, index) => (
                  <tr key={contact._id || index} className="fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                    <td className="contact-name">
                      <div className="name-with-avatar">
                        <div className="avatar" style={{ background: getAvatarColor(contact.inquiryType) }}>
                          {contact.name?.charAt(0) || '?'}
                        </div>
                        {contact.name}
                      </div>
                    </td>
                    <td>
                      <span className={`inquiry-type-badge ${contact.inquiryType}`}>
                        {getInquiryTypeLabel(contact.inquiryType)}
                      </span>
                    </td>
                    <td>
                      <div className="contact-info-cell">
                        <div>{contact.email}</div>
                        <small>{contact.phone}</small>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge status-${contact.status}`} style={{ backgroundColor: getStatusColor(contact.status), color: 'white' }}>
                        <span className="status-dot"></span>
                        {contact.status}
                      </span>
                    </td>
                    <td>
                      <div className="date-cell">
                        {new Date(contact.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="actions-cell">
                      <button 
                        className="btn-view" 
                        onClick={() => viewDetails(contact)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button 
                        className="btn-edit" 
                        onClick={() => openUpdateModal(contact)}
                        title="Update Status"
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-delete" 
                        onClick={() => handleDeleteContact(contact._id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="empty-state">
                    <div className="empty-content">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                      <p>No contacts found</p>
                      <span>Contact inquiries will appear here</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Courses Card */}
      <div className="data-card">
        <div className="card-header">
          <div className="header-left">
            <div className="header-icon courses-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>
              </svg>
            </div>
            <div>
              <h3>Recent Courses</h3>
              <p>Latest additions to your course catalog</p>
            </div>
          </div>
          <button className="view-all-btn" onClick={() => navigateTo('/courses')}>View All →</button>
        </div>
        
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Course Title</th>
                <th>Category</th>
                <th>Duration</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentCourses.length > 0 ? (
                recentCourses.map((course, index) => (
                  <tr key={course._id || index} className="fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                    <td className="course-title">
                      <div className="title-with-icon">
                        <div className="course-badge"></div>
                        {course.title}
                      </div>
                    </td>
                    <td>
                      <span className="category-badge">{course.category || 'General'}</span>
                    </td>
                    <td>
                      <div className="duration-cell">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {course.duration || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${course.isActive ? 'status-active' : 'status-inactive'}`}>
                        <span className="status-dot"></span>
                        {course.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="empty-state">
                    <div className="empty-content">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>
                      </svg>
                      <p>No courses found</p>
                      <span>Add your first course to get started</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Placements Card */}
      <div className="data-card">
        <div className="card-header">
          <div className="header-left">
            <div className="header-icon placements-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <div>
              <h3>Recent Placements</h3>
              <p>Latest success stories from your students</p>
            </div>
          </div>
          <button className="view-all-btn" onClick={() => navigateTo('/placements')}>View All →</button>
        </div>
        
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Course</th>
                <th>Company</th>
                <th>Package</th>
              </tr>
            </thead>
            <tbody>
              {recentPlacements.length > 0 ? (
                recentPlacements.map((placement, index) => (
                  <tr key={placement._id || index} className="fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                    <td className="student-name">
                      <div className="name-with-avatar">
                        <div className="avatar">
                          {placement.studentName?.charAt(0) || 'S'}
                        </div>
                        {placement.studentName}
                      </div>
                    </td>
                    <td>{placement.course || 'N/A'}</td>
                    <td>
                      <div className="company-cell">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                          <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
                        </svg>
                        {placement.company}
                      </div>
                    </td>
                    <td>
                      <span className="package-badge">{placement.package || 'Confidential'}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="empty-state">
                    <div className="empty-content">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                      <p>No placements found</p>
                      <span>Add placement records to showcase success</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedContact && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Contact Details</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="details-section">
                <h3>Personal Information</h3>
                <div className="details-grid">
                  <div><strong>Name:</strong> {selectedContact.name}</div>
                  <div><strong>Email:</strong> <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a></div>
                  <div><strong>Phone:</strong> {selectedContact.phone}</div>
                  <div><strong>Course:</strong> {selectedContact.course || 'Not specified'}</div>
                </div>
              </div>
              
              <div className="details-section">
                <h3>Inquiry Details</h3>
                <div className="details-grid">
                  <div><strong>Type:</strong> {getInquiryTypeLabel(selectedContact.inquiryType)}</div>
                  <div><strong>Status:</strong> <span className="status-badge" style={{ backgroundColor: getStatusColor(selectedContact.status), color: 'white' }}>{selectedContact.status}</span></div>
                  <div><strong>Date:</strong> {new Date(selectedContact.createdAt).toLocaleString()}</div>
                  {selectedContact.preferredDate && (
                    <div><strong>Preferred Date:</strong> {new Date(selectedContact.preferredDate).toLocaleDateString()}</div>
                  )}
                  {selectedContact.timeSlot && (
                    <div><strong>Time Slot:</strong> {selectedContact.timeSlot}</div>
                  )}
                  {selectedContact.numberOfAttendees && (
                    <div><strong>Attendees:</strong> {selectedContact.numberOfAttendees}</div>
                  )}
                </div>
              </div>
              
              {selectedContact.message && (
                <div className="details-section">
                  <h3>Message</h3>
                  <p className="message-content">{selectedContact.message}</p>
                </div>
              )}
              
              {selectedContact.specialRequirements && (
                <div className="details-section">
                  <h3>Special Requirements</h3>
                  <p className="message-content">{selectedContact.specialRequirements}</p>
                </div>
              )}
              
              {selectedContact.adminNotes && (
                <div className="details-section">
                  <h3>Admin Notes</h3>
                  <p className="admin-notes">{selectedContact.adminNotes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Update Modal */}
      {showUpdateModal && selectedContact && (
        <div className="modal-overlay" onClick={() => setShowUpdateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Update Contact Status</h2>
              <button className="modal-close" onClick={() => setShowUpdateModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Status:</label>
                <select 
                  value={updateData.status} 
                  onChange={(e) => setUpdateData({ ...updateData, status: e.target.value })}
                >
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Assign To:</label>
                <input 
                  type="text" 
                  value={updateData.assignedTo} 
                  onChange={(e) => setUpdateData({ ...updateData, assignedTo: e.target.value })}
                  placeholder="Staff name"
                />
              </div>
              
              <div className="form-group">
                <label>Admin Notes:</label>
                <textarea 
                  value={updateData.adminNotes} 
                  onChange={(e) => setUpdateData({ ...updateData, adminNotes: e.target.value })}
                  rows="4"
                  placeholder="Add notes about this inquiry..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowUpdateModal(false)}>Cancel</button>
              <button 
                className="btn-save" 
                onClick={() => handleUpdateStatus(
                  selectedContact._id, 
                  updateData.status, 
                  updateData.adminNotes, 
                  updateData.assignedTo
                )}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;